import type { ActivityStatus } from "@/common/utils/activityStatus";

/**
 * Candidate-local copy of `features/Activity/composables/useActivities.ts`.
 *
 * Copied rather than reused because that composable keeps its list in
 * `useState` under fixed keys ("activities:items", "activities:page", ...) that
 * the canonical `/activities` page also owns. Sharing them would let the
 * canonical list and this candidate overwrite each other's results when a
 * visitor moves between the two routes in one session. Every key here carries
 * the candidate prefix. The request logic itself is unchanged.
 */

export interface PublicActivitiesFilter {
  query?: string;
  provinceId?: number;
  cityId?: number;
  status?: ActivityStatus;
  sort?: "latest" | "oldest" | "nearest_reg" | "oldest_reg" | "finished_recent";
}

// Empty status means "all statuses": the param must not be sent at all.
const buildParams = (
  page: number,
  size: number,
  filter: PublicActivitiesFilter,
): Record<string, unknown> => {
  const params: Record<string, unknown> = { page, size, ...filter };
  if (!params.status) delete params.status;
  return params;
};

interface ActivityResponseItem {
  uuid: string;
  slug: string;
  banner: string;
  bannerUrl: string;
  title: string;
  status: string;
  location: string;
  registrationDate: string;
  registrationClosedDate: string;
  startDate: string;
  endDate: string;
  duration: number;
  createdAt: string;
}

interface ActivitiesResponse {
  data: ActivityResponseItem[];
  meta: { total: number; totalPages: number; page: number; size: number };
}

/** `ActivityItem` is a global type this candidate must not edit, so the row's
 *  own status rides alongside it. Without this the list can only colour cards
 *  by the active filter, which is wrong the moment no filter is active. */
export type PublicActivityItem = ActivityItem & { status: ActivityStatus };

interface ActivitiesPage {
  items: PublicActivityItem[];
  total: number;
  totalPages: number;
  page: number;
}

const toErrorMessage = (err: unknown, fallback: string): string =>
  err instanceof Error ? err.message : fallback;

// "KAB. ACEH SELATAN, ACEH" -> "ACEH SELATAN". The `i` flag matters: the API
// returns mixed-case locations such as "Kota Contoh, Provinsi Contoh".
const extractCity = (location: string): string => {
  if (!location) return "";
  const parts = location.split(",");
  if (parts.length < 1) return location;
  return parts[0].trim().replace(/^(KAB\.|KOTA)\s+/i, "");
};

const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start.toDateString() === end.toDateString()) {
    return new Intl.DateTimeFormat("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(start);
  }

  if (
    start.getMonth() === end.getMonth() &&
    start.getFullYear() === end.getFullYear()
  ) {
    return `${start.getDate()} - ${new Intl.DateTimeFormat("id-ID", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(end)}`;
  }

  return `${new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
  }).format(start)} - ${new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(end)}`;
};

const toActivityItem = (item: ActivityResponseItem): PublicActivityItem => ({
  id: item.uuid,
  // The API recomputes status per row (activity.service.ts getActivityStatusExternal),
  // so this is the authority on what the card should say.
  status: item.status as ActivityStatus,
  to: `/activities/${item.slug}`,
  endDate: item.endDate,
  registrationClosedDate: item.registrationClosedDate,
  isWishlist: false,
  img: item.bannerUrl,
  title: item.title,
  isExpired: new Date(item.registrationClosedDate) < new Date(),
  organizationName: "",
  date: formatDateRange(item.startDate, item.endDate),
  city: extractCity(item.location),
});

interface ActivitiesState {
  activities: Ref<PublicActivityItem[]>;
  isLoading: Ref<boolean>;
  error: Ref<string | null>;
  totalItems: Ref<number>;
  totalPages: Ref<number>;
  currentPage: Ref<number>;
}

export interface PublicActivitiesApi extends ActivitiesState {
  hasMoreActivities: ComputedRef<boolean>;
  fetchActivities: (
    page?: number,
    size?: number,
    filter?: PublicActivitiesFilter,
  ) => Promise<void>;
  loadMore: (size?: number, filter?: PublicActivitiesFilter) => Promise<void>;
}

// Candidate-prefixed keys throughout, so the canonical `/activities` page and
// this route never share list state within one session.
const createState = (): ActivitiesState => ({
  activities: useState<PublicActivityItem[]>("site:activities:items", () => []),
  isLoading: useState<boolean>("site:activities:loading", () => false),
  error: useState<string | null>("site:activities:error", () => null),
  totalItems: useState<number>("site:activities:total", () => 0),
  totalPages: useState<number>("site:activities:total-pages", () => 0),
  currentPage: useState<number>("site:activities:page", () => 1),
});

// A monotonic token shared by the first-page and load-more paths so either can
// invalidate the other. Only the newest token commits, so a slow older response
// cannot overwrite a newer list, raise a stale error, or clear `isLoading`
// early.
interface RequestContext {
  state: ActivitiesState;
  token: { current: number };
  requestPage: (
    page: number,
    size: number,
    filter: PublicActivitiesFilter,
  ) => Promise<ActivitiesPage>;
}

const createFetchActivities =
  (ctx: RequestContext) =>
  async (
    page = 1,
    size = 8,
    filter: PublicActivitiesFilter = {},
  ): Promise<void> => {
    const { state } = ctx;
    const requestId = ++ctx.token.current;
    state.isLoading.value = true;
    state.error.value = null;

    try {
      const result = await ctx.requestPage(page, size, filter);
      if (requestId !== ctx.token.current) return;

      state.activities.value = result.items;
      state.totalItems.value = result.total;
      state.totalPages.value = result.totalPages;
      state.currentPage.value = result.page;
    } catch (err) {
      if (requestId !== ctx.token.current) return;
      // Surfaced to the visitor through the page's alert; nothing is swallowed.
      state.error.value = toErrorMessage(err, "Failed to load activities");
      state.activities.value = [];
    } finally {
      if (requestId === ctx.token.current) state.isLoading.value = false;
    }
  };

const createLoadMore =
  (ctx: RequestContext) =>
  async (size = 8, filter: PublicActivitiesFilter = {}): Promise<void> => {
    const { state } = ctx;
    if (
      state.isLoading.value ||
      state.currentPage.value >= state.totalPages.value
    ) {
      return;
    }

    const nextPage = state.currentPage.value + 1;
    const requestId = ++ctx.token.current;
    state.isLoading.value = true;

    try {
      const result = await ctx.requestPage(nextPage, size, filter);
      if (requestId !== ctx.token.current) return;

      // Append, never replace: a newer first-page fetch would have bumped the
      // token and this branch would not be reached.
      state.activities.value = [...state.activities.value, ...result.items];
      state.currentPage.value = result.page;
    } catch (err) {
      if (requestId !== ctx.token.current) return;
      state.error.value = toErrorMessage(err, "Failed to load more activities");
    } finally {
      if (requestId === ctx.token.current) state.isLoading.value = false;
    }
  };

export function usePublicActivities(): PublicActivitiesApi {
  const { $customFetch } = useNuxtApp();
  const state = createState();

  const requestPage = async (
    page: number,
    size: number,
    filter: PublicActivitiesFilter,
  ): Promise<ActivitiesPage> => {
    const response = await $customFetch<ActivitiesResponse>(
      "/public-activities",
      { method: "GET", params: buildParams(page, size, filter) },
    );

    return {
      items: response.data.map(toActivityItem),
      total: response.meta.total,
      totalPages: response.meta.totalPages,
      page: response.meta.page,
    };
  };

  const ctx: RequestContext = { state, token: { current: 0 }, requestPage };

  return {
    ...state,
    hasMoreActivities: computed(
      () => state.currentPage.value < state.totalPages.value,
    ),
    fetchActivities: createFetchActivities(ctx),
    loadMore: createLoadMore(ctx),
  };
}
